import React, { cloneElement, useState, useEffect, useContext } from 'react';
import { useIntl } from 'react-intl';

export const FormContext = React.createContext(null);

/**
 * Chowbus Form is a minimal form framework
 * TODO: document this
 * @param {*} props
 */
export function Form(props) {
  const { children, onSubmit, defaultBehavior, name = 'ChowbusForm', ...rest } = props;
  const [fields, setFields] = useState({});
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState({});

  const isValid = !Object.values(errors).some((error) => !!error);

  const onFormSubmit = (e) => {
    onSubmit && onSubmit(fields, e);
  };

  return (
    <FormContext.Provider
      value={{
        fields,
        setFields,
        errors,
        setErrors,
        dirty,
        setDirty,
        onSubmit: onFormSubmit,
        isValid,
      }}>
      <form
        name={name}
        onSubmit={(e) => {
          if (!defaultBehavior) {
            e.preventDefault();
          }
          onFormSubmit(e);
        }}
        {...rest}>
        {children}
      </form>
    </FormContext.Provider>
  );
}

/**
 * Chowbus Field wraps inputs in a Chowbus Form to keep form state in sync with inputs
 * It is a HOC (Higher Order Component) that passes form context down to it's children.
 *
 * example:
 * ```
  <Field
    name='city_selection'
    required
    validate={[lengthValidator({ min: 1 ]}>
    {(fieldContext) => (
      <ChowbusSelect
        label='City?'
        name={fieldContext.name}
        value={fieldContext.fields[fieldContext.name]}
        onChange={fieldContext.onChange}
        fullWidth>
          <MenuItem value='chicago'>Chicago</MenuItem>
          <MenuItem value='nyc'>New York City</MenuItem>
      </ChowbusSelect>
    )}
  </Field>
 * ```
 * @param {*} props
 */
export function Field(props) {
  const { children, name, initValue, validate, required } = props;
  const { fields, setFields, errors, setErrors, dirty, setDirty, isValid, onSubmit } = useContext(
    FormContext
  );
  const intl = useIntl();

  useEffect(() => {
    initValue && setValue(initValue);
  }, []);

  const setError = (error) => {
    if (!name) {
      return;
    }
    setErrors((errors) => ({
      ...errors,
      ...{
        [name]: error,
      },
    }));
  };
  const setValue = ({ value } = {}, userInput = false) => {
    if (!name) {
      return;
    }
    let errors = [];
    if (required && !value) {
      errors.push('required');
    } else if (validate) {
      const validators = Array.isArray(validate) ? validate : [validate];
      validators.forEach((validator) => {
        const error = validator(value, fields, intl);
        if (error) {
          errors.push(error);
        }
      });
    }
    if (errors.length) {
      setError(errors.join(', '));
    } else {
      setError(null);
    }
    if (userInput) {
      setDirty((dirty) => ({
        ...dirty,
        ...{
          [name]: true,
        },
      }));
    }
    setFields((fields) => ({
      ...fields,
      ...{
        [name]: value,
      },
    }));
  };

  const onChange = (e) => {
    if (!e) {
      return;
    }
    const value = e.target?.value;
    setValue({ value }, true);
  };

  const error = !!errors[name] && dirty[name];

  const childProps = {
    name,
    onChange,
    error,
    errorMessage: errors[name],
    isValid,
    isFieldDirty: dirty[name],
    fieldValue: fields[name],
    onSubmit,
    fields,
  };
  if (typeof children === 'function') {
    return children(childProps);
  } else {
    return cloneElement(children, childProps);
  }
}
